import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { toast } from "react-toastify";
import { isMobile } from "react-device-detect";

/* ────────────────────────────────────────────────────────────
   UI ONLY — no API calls.
   All data lives in local component state. Every place where a
   backend call will be needed is marked with:  // TODO(API)
   ──────────────────────────────────────────────────────────── */

export type TeamRole = "admin" | "member";

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  username?: string;
  avatar_url?: string;
  role: TeamRole | "owner";
  is_you?: boolean;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: TeamRole;
  invited_at: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** true only for the highest plan ($199). Others see the upgrade prompt. */
  isEligible: boolean;
  onUpgrade: () => void;
  /** Logged-in user (shown as the owner row). Pass values from userDets. */
  owner?: { name?: string; email?: string; username?: string; avatar_url?: string };
}

const MAX_TEAM_MEMBERS = 5; // excludes the owner

/* ── helpers / shared styles ─────────────────────────────── */
const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "?";

const roleLabel = (r: string) => (r === "owner" ? "Owner" : r === "admin" ? "Admin" : "Member");

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    borderRadius: "10px",
    background: "rgba(255,255,255,0.04)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
    "&.Mui-focused fieldset": { borderColor: "#4BA5EB" },
  },
  "& input::placeholder": { color: "rgba(255,255,255,0.4)", opacity: 1 },
};

const selectSx = {
  color: "#fff",
  fontSize: 14,
  fontWeight: 500,
  borderRadius: "10px",
  background: "rgba(255,255,255,0.04)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.14)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" },
};

const menuPaperSx = {
  PaperProps: {
    sx: {
      bgcolor: "#171A22",
      color: "#fff",
      border: "1px solid rgba(255,255,255,0.08)",
      "& .MuiMenuItem-root": { fontSize: 14 },
    },
  },
};

const countBadgeSx = {
  px: 0.9,
  borderRadius: "4px",
  fontSize: 12,
  background: "rgba(255,255,255,0.1)",
  color: "#fff",
};

const Avatar = ({ name, url }: { name: string; url?: string }) =>
  url ? (
    <Box
      component="img"
      src={url}
      alt={name}
      sx={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
    />
  ) : (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg,#5B3FBF,#8B5CF6)",
        color: "#fff",
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      {initials(name)}
    </Box>
  );

/* ── component ───────────────────────────────────────────── */
const TeamMembersModal: React.FC<Props> = ({ open, onClose, isEligible, onUpgrade, owner }) => {
  const [tab, setTab] = useState<"members" | "pending">("members");

  // Local state only (replace with API data later)
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<TeamRole>("member");

  const ownerRow: TeamMember = {
    id: "owner",
    name: owner?.name?.trim() || "You",
    email: owner?.email || "",
    username: owner?.username,
    avatar_url: owner?.avatar_url,
    role: "owner",
    is_you: true,
  };
  const allMembers = [ownerRow, ...members];

  const usedSeats = members.length + pending.length;
  const seatsFull = usedSeats >= MAX_TEAM_MEMBERS;
  const emailTrim = email.trim().toLowerCase();
  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim), [emailTrim]);
  const alreadyAdded =
    emailTrim !== "" &&
    (emailTrim === ownerRow.email.toLowerCase() ||
      members.some((m) => m.email.toLowerCase() === emailTrim) ||
      pending.some((p) => p.email.toLowerCase() === emailTrim));

  /* ── handlers (swap the bodies for API calls later) ───── */
  const handleInvite = () => {
    if (!emailValid || seatsFull || alreadyAdded) return;
    // TODO(API): POST /api/team/invites  { email, role }
    setPending((p) => [
      ...p,
      { id: `inv_${Date.now()}`, email: emailTrim, role: inviteRole, invited_at: new Date().toISOString() },
    ]);
    setEmail("");
    setTab("pending");
    toast.success(`Invitation sent to ${emailTrim}`);
  };

  const handleRoleChange = (id: string, role: TeamRole) => {
    // TODO(API): PATCH /api/team/members/:id  { role }
    setMembers((list) => list.map((m) => (m.id === id ? { ...m, role } : m)));
  };

  const handleRemoveMember = (m: TeamMember) => {
    if (!window.confirm(`Remove ${m.name} from your team?`)) return;
    // TODO(API): DELETE /api/team/members/:id
    setMembers((list) => list.filter((x) => x.id !== m.id));
  };

  const handleResend = (inv: PendingInvite) => {
    // TODO(API): POST /api/team/invites/:id/resend
    toast.success(`Invitation resent to ${inv.email}`);
  };

  const handleCancelInvite = (inv: PendingInvite) => {
    // TODO(API): DELETE /api/team/invites/:id
    setPending((list) => list.filter((x) => x.id !== inv.id));
  };

  const handleCreateLink = () => {
    // TODO(API): POST /api/team/invite-link  { role }
    setInviteLink(`${window.location.origin}/join/${Math.random().toString(36).slice(2, 10)}`);
  };

  const handleDisableLink = () => {
    // TODO(API): DELETE /api/team/invite-link
    setInviteLink(null);
  };

  const copyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };

  /* ── render ───────────────────────────────────────────── */
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="team-modal-title"
      sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" } }}
    >
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "94%" : 620,
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "#0F1117",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
          borderRadius: "16px",
          p: { xs: 2.5, md: 3.5 },
          outline: "none",
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2.5 }}>
          <Box>
            <Typography id="team-modal-title" sx={{ color: "#fff", fontSize: 22, fontWeight: 700 }}>
              Team members
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13, mt: 0.4 }}>
              Invite up to {MAX_TEAM_MEMBERS} teammates to work in your workspace.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)", mt: -0.5, mr: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Locked state (plans below the $199 tier) */}
        {!isEligible ? (
          <Box
            sx={{
              textAlign: "center",
              p: 4,
              borderRadius: "12px",
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.03)",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 36, color: "#8B5CF6", mb: 1 }} />
            <Typography sx={{ color: "#fff", fontSize: 16, fontWeight: 600, mb: 0.8, textAlign: 'left' }}>
              {/* Team access is available on our highest plan */}
              Team access is available for the plan Scale and above, please upgrade it.
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13.5, mb: 2.5 }}>
              Upgrade to the $199 plan to invite and manage up to {MAX_TEAM_MEMBERS} team members.
            </Typography>
            <Button
              onClick={() => {
                onClose();
                onUpgrade();
              }}
              sx={{
                background: "linear-gradient(90deg, #0168B5, #4BA5EB)",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                "&:hover": { opacity: 0.9 },
              }}
            >
              View plans
            </Button>
          </Box>
        ) : (
          <>
            {/* Invite row */}
            <Stack direction={isMobile ? "column" : "row"} spacing={1.2} alignItems="stretch">
              <TextField
                fullWidth
                size="small"
                placeholder="Email address"
                value={email}
                disabled={seatsFull}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                error={alreadyAdded}
                sx={fieldSx}
              />
              <Select
                size="small"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as TeamRole)}
                MenuProps={menuPaperSx}
                sx={{ ...selectSx, minWidth: 120 }}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
              <Button
                onClick={handleInvite}
                disabled={!emailValid || seatsFull || alreadyAdded}
                variant="contained"
                sx={{
                  background: "linear-gradient(90deg, #0168B5, #4BA5EB)",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  boxShadow: "none",
                  "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                Share
              </Button>
            </Stack>
            {alreadyAdded && (
              <Typography sx={{ color: "#f87171", fontSize: 12.5, mt: 1 }}>
                This email is already on your team or has a pending invite.
              </Typography>
            )}
            {seatsFull && (
              <Typography sx={{ color: "#F59E0B", fontSize: 12.5, mt: 1 }}>
                You've used all {MAX_TEAM_MEMBERS} seats. Remove a member or cancel an invite to add someone new.
              </Typography>
            )}

            {/* Share with link */}
            <Stack direction="row" spacing={1.6} alignItems="center" sx={{ mt: 3 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LinkIcon sx={{ color: "rgba(255,255,255,0.8)" }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: "#fff", fontSize: 14.5, fontWeight: 500 }}>
                  Share this workspace with a link
                </Typography>
                {inviteLink ? (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.3 }}>
                    <Typography noWrap sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12.5, maxWidth: 260 }}>
                      {inviteLink}
                    </Typography>
                    <Tooltip title="Copy link">
                      <IconButton size="small" onClick={copyLink} sx={{ color: "#4BA5EB" }}>
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      onClick={handleDisableLink}
                      sx={{ color: "#f87171", fontSize: 12.5, cursor: "pointer", fontWeight: 500 }}
                    >
                      Disable
                    </Typography>
                  </Stack>
                ) : (
                  <Typography
                    onClick={seatsFull ? undefined : handleCreateLink}
                    sx={{
                      display: "inline-block",
                      color: seatsFull ? "rgba(255,255,255,0.3)" : "#4BA5EB",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: seatsFull ? "not-allowed" : "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Create link
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Tabs */}
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                mt: 3,
                minHeight: 40,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                "& .MuiTabs-indicator": { backgroundColor: "#4BA5EB", height: 2 },
                "& .MuiTab-root": {
                  textTransform: "none",
                  color: "rgba(255,255,255,0.55)",
                  fontWeight: 600,
                  fontSize: 15,
                  minHeight: 40,
                  px: 0,
                  mr: 3,
                  minWidth: 0,
                },
                "& .Mui-selected": { color: "#4BA5EB !important" },
              }}
            >
              <Tab
                value="members"
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>Team members</span>
                    <Box sx={countBadgeSx}>
                      {members.length}/{MAX_TEAM_MEMBERS}
                    </Box>
                  </Stack>
                }
              />
              <Tab
                value="pending"
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>Pending invites</span>
                    {pending.length > 0 && <Box sx={countBadgeSx}>{pending.length}</Box>}
                  </Stack>
                }
              />
            </Tabs>

            {/* List */}
            <Box sx={{ mt: 1.5, minHeight: 120 }}>
              {tab === "members" ? (
                allMembers.map((m, i) => (
                  <React.Fragment key={m.id}>
                    {i > 0 && <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />}
                    <Stack direction="row" alignItems="center" spacing={1.6} sx={{ py: 1.5 }}>
                      <Avatar name={m.name} url={m.avatar_url} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ color: "#fff", fontSize: 15, fontWeight: 500 }}>
                          {m.name}
                          {m.is_you ? " (you)" : ""}
                        </Typography>
                        <Typography noWrap sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12.5 }}>
                          {m.username ? `@${m.username} • ` : ""}
                          {m.role === "owner" ? "Workspace owner" : m.email}
                        </Typography>
                      </Box>

                      {m.role === "owner" ? (
                        <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 13.5, px: 1.5 }}>Owner</Typography>
                      ) : (
                        <>
                          <Select
                            size="small"
                            value={m.role}
                            onChange={(e) => handleRoleChange(m.id, e.target.value as TeamRole)}
                            MenuProps={menuPaperSx}
                            sx={{ ...selectSx, minWidth: 110 }}
                          >
                            <MenuItem value="member">Member</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                          </Select>
                          <Tooltip title="Remove from team">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveMember(m)}
                              sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#f87171" } }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </React.Fragment>
                ))
              ) : pending.length ? (
                pending.map((inv, i) => (
                  <React.Fragment key={inv.id}>
                    {i > 0 && <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />}
                    <Stack direction="row" alignItems="center" spacing={1.6} sx={{ py: 1.5 }}>
                      <Avatar name={inv.email} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ color: "#fff", fontSize: 15, fontWeight: 500 }}>
                          {inv.email}
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12.5 }}>
                          {roleLabel(inv.role)} • Invited {new Date(inv.invited_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => handleResend(inv)}
                        sx={{ textTransform: "none", color: "#4BA5EB", fontWeight: 600 }}
                      >
                        Resend
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleCancelInvite(inv)}
                        sx={{ textTransform: "none", color: "#f87171", fontWeight: 600 }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </React.Fragment>
                ))
              ) : (
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13.5, py: 3 }}>
                  No pending invitations.
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default TeamMembersModal;
